import { err, ok, Result } from "neverthrow";
import GraphqlApiError from "@/framework/network/gql/GraphqlApiError";
import { GroupMember } from "@/modules/staff/objects/group/GroupMember";
import { GraphqlApi, IGraphqlApi } from "@/framework/network/gql/GraphqlApi";
import {
  BlockConversationMemberDocument,
  CreateGroupConversationInput,
  CreateGroupConversationV2Document,
  CreateGroupConversationV2Mutation,
  DeclineInvitationConversationDocument,
  DeclineInvitationConversationMutation,
  DeleteConversationMemberDocument,
  GetBroadcastStatusDocument,
  GetConversationSettingsDocument,
  GetConversationSettingsQuery,
  GetConversationV2Document,
  GetGroupConversationOverviewDocument,
  GetGroupMembersDocument,
  GetUserDefaultLimitDocument,
  InviteGroupConversationDocument,
  JoinBizTenantDocument,
  JoinGroupConversationDocument,
  ListContactUserDocument,
  NotifyMyselfDocument,
  UnblockConversationMemberDocument,
  BlockConversationMemberMutation,
  UpdateConversationDocument,
  UpdateConversationV2Document,
  UpdateGroupConversationChild,
  UpdateGroupConversationInput,
  DeleteConversationMemberMutation,
  InviteGroupConversationMutation,
  JoinGroupConversationMutation,
  NotifyMyselfMutation,
  GetGroupConversationOverviewQuery,
  GetGroupMembersQuery,
  GetConversationV2Query,
  ListContactUserQuery,
  UnblockConversationMemberMutation,
  UpdateConversationMutation,
  UpdateConversationV2Mutation,
  GetUserDefaultLimitQuery,
  GetBroadcastStatusQuery,
} from "@/framework/network/gql/generated/graphql";
import { GeneralIdResponseSchema, GeneralIdResponse } from "@/framework/objects/GeneralIdResponse";
import { MediaService } from "@/framework/media/MediaService";
import { GroupProfile, GroupProfileSchema } from "@/modules/staff/objects/group/GroupProfile";
import { InviteApprovalType } from "@/modules/staff/objects/group/InviteApprovalType";
import { NotificationRange } from "@/modules/staff/objects/group/NotificationRange";
import { QcConversationSettings } from "@/modules/staff/objects/group/QcConversationSettings";
import { QcConversationSetting } from "@/modules/staff/objects/group/QcConversationSetting";
import { MemberRole } from "@/modules/staff/objects/group/MemberRole";
import { JoinStatus } from "@/modules/staff/objects/group/JoinStatus";
import { MemberLimit } from "@/modules/staff/objects/group/MemberLimit";
import { ErrorService } from "@/framework/errors/ErrorService";
import { QcConversation, QcConversationSchema } from "@/modules/staff/objects/conversation/QcConversation";
import { BroadcastStatus, BroadcastStatusSchema } from "@/modules/staff/objects/conversation/BroadcastStatus";
import { JoinGroupConversationInput } from "@/modules/staff/objects/conversation/JoinGroupConversationInput";
import { UserService } from "@/modules/auth/services/UserService";
import { i18n } from "@/framework/plugins/i18n";
import { GeneralResponse, GeneralResponseSchema } from "@/framework/objects/GeneralResponse";
import { NotifyMyselfResponse, NotifyMyselfResponseSchema } from "@/modules/staff/objects/group/NotifyMyselfResponse";
import { GroupMembers, GroupMembersSchema } from "@/modules/staff/objects/group/GroupMembers";
import { ModelUserContactList, ModelUserContactListSchema } from "@/modules/staff/objects/guest/ModelUserContactList";
import { QcConversationMember } from "@/modules/staff/objects/group/QcConversationMember";

export class GroupService {
  public oneWayGroupKey = "one_way";
  public interactiveGroupKey = "interactive";
  private _graphql: IGraphqlApi;

  public constructor(graphql: IGraphqlApi = new GraphqlApi()) {
    this._graphql = graphql;
  }

  loadGroup = async (id: string) => {
    try {
      const res = await this.loadConversation(id);
      if (res.isErr()) {
        return await new ErrorService().handleError(res.error);
      }
      let conversation = res.value;
      let convMembers = conversation.members.items;
      let media = new MediaService();
      let group: GroupProfile = {
        id: conversation.id,
        name: conversation.name,
        icon: media.resizeCloudinaryImage(conversation.icon, 500) || "",
        qr: "",
        description: conversation.description,
        memberLimitInfo: conversation.memberLimitInfo || { current: 0, limit: 0 },
        members: convMembers.map((member) => {
          return {
            accessId: member.id.toString(),
            idAccount: member.id_account,
            inviteUserIdAccount: member.invite_user_id_account,
            name: member.user.name,
            icon: member.user.icon,
            qr: member.qr,
            role: member.role,
            joinStatus: member.join_status,
            signUpAt: member.user.sign_up_at,
            notificationRange: member.notification_range,
          };
        }),
        inviteApprovalType: InviteApprovalType.None,
        notificationRange: NotificationRange.None,
        is_anonymous_join: conversation.is_anonymous_join || false,
      };

      return group;
    } catch (error) {
      return null;
    }
  };

  loadGroupV2 = async (
    id: string
  ): Promise<Result<{ [key: string]: { profile: GroupProfile; uses: boolean } }, GraphqlApiError>> => {
    const res = await this._graphql.runQueryAndValidate<GetConversationSettingsQuery, QcConversationSettings>(
      GetConversationSettingsDocument,
      GroupProfileSchema,
      { id }
    );

    if (res.isOk()) {
      const groups: QcConversationSettings = res.value;
      const group: GroupProfile = groups.group ? this.convertToGroupProfile(groups.group, groups) : groups.group;
      const oneWayGroup: GroupProfile = groups.oneWayGroup
        ? this.convertToGroupProfile(groups.oneWayGroup, groups)
        : groups.oneWayGroup;
      return ok({
        [this.interactiveGroupKey]: {
          profile: group || oneWayGroup,
          uses: groups.groupEnabled,
        },
        [this.oneWayGroupKey]: {
          profile: oneWayGroup,
          uses: groups.oneWayGroupEnabled,
        },
      });
    } else {
      return err(res.error);
    }
  };

  async createGroupConversationV2(
    input: CreateGroupConversationInput
  ): Promise<Result<GeneralIdResponse, GraphqlApiError>> {
    const result = await this._graphql.runMutationAndValidate<CreateGroupConversationV2Mutation, GeneralIdResponse>(
      CreateGroupConversationV2Document,
      GeneralIdResponseSchema,
      { input: input }
    );

    if (result.isErr()) {
      return err(result.error);
    } else {
      return ok(result.value);
    }
  }

  async declineInvitationConversation(conversationId: string): Promise<Result<GeneralResponse, GraphqlApiError>> {
    const result = await this._graphql.runMutationAndValidate<DeclineInvitationConversationMutation, GeneralResponse>(
      // @ts-ignore - because TS issue with generic typing of input?
      DeclineInvitationConversationDocument,
      GeneralResponseSchema,
      { conversationId: conversationId } as { conversationId: string }
    );

    if (result.isErr()) {
      return err(result.error);
    } else {
      return ok(result.value);
    }
  }

  async blockMember(conversationId: string, member: GroupMember): Promise<Result<GeneralResponse, GraphqlApiError>> {
    const result = await this._graphql.runMutationAndValidate<BlockConversationMemberMutation, GeneralResponse>(
      // @ts-ignore - because TS issue with generic typing of input?
      BlockConversationMemberDocument,
      GeneralResponseSchema,
      {
        conversationId: conversationId,
        userId: member.accessId,
      }
    );

    if (result.isErr()) {
      return err(result.error);
    } else {
      return ok(result.value);
    }
  }

  async deleteConversationMember(
    conversationId: string,
    member: GroupMember,
    // member: QcConversationMember,
    userId: string = ""
  ): Promise<Result<GeneralResponse, GraphqlApiError>> {
    const result = await this._graphql.runMutationAndValidate<DeleteConversationMemberMutation, GeneralResponse>(
      // @ts-ignore - because TS issue with generic typing of input?
      DeleteConversationMemberDocument,
      GeneralResponseSchema,
      {
        conversationId: conversationId,
        userId: userId ? userId : member.accessId,
      }
    );

    if (result.isErr()) {
      return err(result.error);
    } else {
      return ok(result.value);
    }
  }

  async inviteGroupMember(
    conversationId: string,
    userId: string,
    role?: string
  ): Promise<Result<GeneralResponse, GraphqlApiError>> {
    const result = await this._graphql.runMutationAndValidate<InviteGroupConversationMutation, GeneralResponse>(
      // @ts-ignore - because TS issue with generic typing of input?
      InviteGroupConversationDocument,
      GeneralResponseSchema,
      {
        conversationId: conversationId,
        userId: userId,
        role: role,
      }
    );

    if (result.isErr()) {
      return err(result.error);
    } else {
      return ok(result.value);
    }
  }

  async addGroupMember(
    conversationId: string,
    inviteUserId?: string,
    joinUserId?: string
  ): Promise<Result<GeneralResponse, GraphqlApiError>> {
    const result = await this._graphql.runMutationAndValidate<JoinGroupConversationMutation, GeneralResponse>(
      // @ts-ignore - because TS issue with generic typing of input?
      JoinGroupConversationDocument,
      GeneralResponseSchema,
      {
        conversationId: conversationId,
        joinUserId: joinUserId,
        inviteUserId: inviteUserId,
      }
    );

    if (result.isErr()) {
      return err(result.error);
    } else {
      return ok(result.value);
    }
  }

  // this was previously in the old group service/helper/api-keeper script (as "notifyMyself"), so moving it here for consistency
  async notifyMyself(
    event_type?: string,
    event_value?: string[] | string,
    user_access_id?: string
  ): Promise<Result<NotifyMyselfResponse, GraphqlApiError>> {
    const result = await this._graphql.runMutationAndValidate<NotifyMyselfMutation, NotifyMyselfResponse>(
      NotifyMyselfDocument,
      NotifyMyselfResponseSchema,
      {
        user_access_id,
        event_type,
        event_value,
      }
    );

    if (result.isErr()) {
      return err(result.error);
    } else {
      return ok(result.value);
    }
  }

  convertToGroupProfile = (group: QcConversationSetting, groups: QcConversationSettings): GroupProfile => {
    // const members: QcConversationMember[] = group.members.items;
    const members: GroupMember[] = group.members.items;

    const memberLimit: MemberLimit = {
      current: group.memberLimitInfo.current,
      limit: group.memberLimitInfo.limit,
    };

    const inviteApprovalType: InviteApprovalType =
      InviteApprovalType[this.convertToCamel(groups.inviteApprovalType) as keyof typeof InviteApprovalType];
    const notificationRange: NotificationRange =
      NotificationRange[this.convertToCamel(groups.notificationRange) as keyof typeof NotificationRange];

    return {
      id: group.id,
      name: group.name,
      description: group.description,
      icon: group.icon,
      qr: "",
      memberLimitInfo: memberLimit,
      members,
      inviteApprovalType,
      notificationRange,
      is_anonymous_join: groups.isAnonymousJoin,
    };
  };

  loadGroupForInvitation = async (
    groupId: string,
    page = 1,
    limit = 100
  ): Promise<Result<GroupProfile, GraphqlApiError>> => {
    const res = await this._graphql.runQueryAndValidate<GetGroupConversationOverviewQuery, GroupProfile>(
      GetGroupConversationOverviewDocument,
      GroupProfileSchema,
      {
        groupId,
        page,
        limit,
      }
    );

    if (res.isErr()) {
      return err(res.error);
    }

    const groupRes = res.value;
    const membersRes = await this.getGroupMembers(groupRes.id, 1, 100);

    if (membersRes.isErr()) {
      return err(membersRes.error);
    }

    let group: GroupProfile = {
      id: groupRes.id,
      name: groupRes.name,
      icon: new MediaService().resizeCloudinaryImage(groupRes.icon, 500) || "",
      qr: "",
      description: groupRes.description,
      memberLimitInfo: groupRes.memberLimitInfo,
      members: membersRes.value.members,
      inviteApprovalType: InviteApprovalType.None,
      notificationRange: NotificationRange.None,
      is_anonymous_join: groupRes.is_anonymous_join,
    };

    return ok(group);
  };

  loadContactList = async (): Promise<Result<GroupMember[], GraphqlApiError>> => {
    const response = await this._graphql.runQueryAndValidate<ListContactUserQuery, ModelUserContactList>(
      ListContactUserDocument,
      ModelUserContactListSchema,
      { limit: 100, page: 1 }
    );

    if (response.isErr()) {
      return err(response.error);
    }

    const contactListResponse = response.value;
    const res: GroupMember[] = [];
    let media = new MediaService();
    contactListResponse.items.forEach((item: any) => {
      res.push({
        accessId: item.access_id,
        idAccount: item.id_account,
        inviteUserIdAccount: 0,
        name: item.name,
        icon: media.resizeCloudinaryImage(item.icon, 250) || "",
        qr: item.qr,
        role: MemberRole.Member,
        joinStatus: JoinStatus.WaitJoin,
        signUpAt: item.sign_up_at,
        notificationRange: NotificationRange.All,
      });
    });

    return ok(res);
  };

  createGroup = async (
    group: GroupProfile,
    groupEnabled: boolean,
    oneWay: GroupProfile,
    oneWayGroupEnabled: boolean,
    isInvitation: boolean
  ) => {
    const interactiveGroup = group ? { name: group.name, description: group.description, icon: group.icon } : null;
    const oneWayGroup = group ? { name: oneWay.name, description: oneWay.description, icon: oneWay.icon } : null;
    const request_param = {
      inviteApprovalType: group.inviteApprovalType,
      notificationRange: group.notificationRange,
      isAnonymousJoin: group.is_anonymous_join,
      groupEnabled: groupEnabled,
      group: interactiveGroup,
      oneWayGroupEnabled: oneWayGroupEnabled,
      oneWayGroup: oneWayGroup,
    };
    try {
      const createGroupRes = await this.createGroupConversationV2(request_param);
      let conversationId: string;
      if (createGroupRes.isOk()) conversationId = createGroupRes.value.id as string;
      // unreturned id is already treated as an error in the API call
      else return err(createGroupRes.error);

      if (isInvitation) {
        group.members.forEach((member) => {
          if (member.role !== MemberRole.Owner) {
            // this.inviteGroupMember(conversationId, member.user.access_id, member.role);
            this.inviteGroupMember(conversationId, member.accessId, member.role);
          }
        });
      } else {
        const user = await new UserService().getCachedQcUser();
        if (user.isErr()) {
          console.error("Failed to get user info");
          return err(user.error);
        }
        const res = [];

        for (let i = 0; i < group.members.length; i++) {
          const member = group.members[i];
          if (member.role !== MemberRole.Owner) {
            // res.push(this.addGroupMember(conversationId, member.user.access_id, user.value.username));
            res.push(this.addGroupMember(conversationId, member.accessId, user.value.username));
          }
        }
        await Promise.all(res);
      }
      return ok(conversationId);
    } catch (error) {
      console.error(error);
      return err(error);
    }
  };

  unblockGroupMember = async (conversationId: string, member: GroupMember) => {
    try {
      const res = await this._graphql.runMutationAndValidate<UnblockConversationMemberMutation, GeneralResponse>(
        // @ts-ignore issue with generic typing and Exact
        UnblockConversationMemberDocument,
        GeneralResponseSchema,
        { conversationId: conversationId, userId: member.accessId }
      );

      if (res.isErr()) {
        return err(res.error);
      } else {
        return ok(res.value);
      }
    } catch (error) {
      console.log(error);
      return err(error);
    }
  };

  updateGroupProfile = async (param: {}): Promise<Result<GeneralResponse, GraphqlApiError>> => {
    try {
      const res = await this._graphql.runMutationAndValidate<UpdateConversationMutation, GeneralResponse>(
        // @ts-ignore issue with generic typing and Exact
        UpdateConversationDocument,
        GeneralResponseSchema,
        param
      );
      if (res.isErr()) {
        return err(res.error);
      } else {
        return ok(res.value);
      }
    } catch (error: any) {
      console.log(error);
      return err(error);
    }
  };

  updateGroupProfileV2 = async (
    inviteApprovalType: string,
    notificationRange: string,
    profileInteractive?: GroupProfile,
    profileOneWay?: GroupProfile
  ): Promise<Result<GeneralResponse, GraphqlApiError>> => {
    const group: UpdateGroupConversationChild | null = profileInteractive
      ? {
          conversationId: profileInteractive.id,
          name: profileInteractive.name,
          description: profileInteractive.description,
          icon: profileInteractive.icon,
        }
      : null;

    const oneWayGroup: UpdateGroupConversationChild | null = profileOneWay
      ? {
          conversationId: profileOneWay.id,
          name: profileOneWay.name,
          description: profileOneWay.description,
          icon: profileOneWay.icon,
        }
      : null;

    const param: { input: UpdateGroupConversationInput } = {
      input: { inviteApprovalType, notificationRange, group, oneWayGroup },
    };

    try {
      const res = await this._graphql.runMutationAndValidate<UpdateConversationV2Mutation, GeneralResponse>(
        UpdateConversationV2Document,
        GeneralResponseSchema,
        param
      );
      if (res.isErr()) {
        return err(res.error);
      } else {
        return ok(res.value);
      }
    } catch (error: any) {
      console.log(error);
      return err(error);
    }
  };

  getMemberLimit = async (
    page?: number,
    limit?: number,
    isBlocked?: boolean
  ): Promise<Result<number, GraphqlApiError>> => {
    try {
      const res = await this._graphql.runQueryAndValidate<GetUserDefaultLimitQuery, GeneralResponse>(
        // @ts-ignore issue with generic typing and Exact
        GetUserDefaultLimitDocument,
        GeneralResponseSchema,
        { page, limit, isBlocked }
      );

      if (res.isErr()) {
        return err(res.error);
      }
      const response = res.value;
      return ok(response.default_limit || 0);
    } catch (error) {
      console.log(error);
    }
    return ok(10);
  };

  getGroupMembers = async (
    conversationId: string,
    page: number = 1,
    limit: number = 100
  ): Promise<Result<GroupMembers, GraphqlApiError>> => {
    const res = await this._graphql.runQueryAndValidate<GetGroupMembersQuery, GroupMembers>(
      // @ts-ignore generic type causing issue with document exact type def
      GetGroupMembersDocument,
      GroupMembersSchema,
      {
        id: conversationId,
        page: page,
        limit: limit,
      }
    );

    if (res.isErr()) {
      return err(res.error);
    }

    return ok(res.value);
  };

  loadConversation = async (conversationId: string): Promise<Result<QcConversation, GraphqlApiError>> => {
    const res = await this._graphql.runQueryAndValidate<GetConversationV2Query, QcConversation>(
      GetConversationV2Document,
      QcConversationSchema,
      {
        id: conversationId,
        messageLimit: 20,
      }
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  };

  // 定数だと翻訳されないことがある
  getMemberRoleItems = () => {
    return [
      {
        value: MemberRole.Owner,
        text: `${i18n.t("owner")}`,
      },
      {
        value: MemberRole.Member,
        text: `${i18n.t("member")}`,
      },
      {
        value: MemberRole.Guest,
        text: `${i18n.t("guest")}`,
      },
    ];
  };

  isOneWayGroup = (item: QcConversation) => item && item.conversation_type === "group_one_way";

  isOneWayGroupConvType = (conversationType: string) => conversationType === "group_one_way";

  getBroadcastStatus = async (broadcastId: string): Promise<Result<BroadcastStatus, GraphqlApiError>> => {
    const res = await this._graphql.runQueryAndValidate<GetBroadcastStatusQuery, BroadcastStatus>(
      // @ts-ignore generic type causing issue with document exact type def
      GetBroadcastStatusDocument,
      BroadcastStatusSchema,
      { broadcastId: broadcastId }
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  };

  joinGroupConversation = async (
    invitation: JoinGroupConversationInput
  ): Promise<Result<GeneralResponse, GraphqlApiError>> => {
    const res = await this._graphql.runMutationAndValidate<JoinGroupConversationMutation, GeneralResponse>(
      // @ts-ignore generic type causing issue with document exact type def
      JoinGroupConversationDocument,
      GeneralResponseSchema,
      invitation
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  };

  // { tenant_code: tenantCode,
  //   secret_key: secretKey}
  joinTenant = async (v: any) => {
    const res = await this._graphql.runMutation(JoinBizTenantDocument, v);
    return res.isErr() ? err(res.error) : ok(res.value);
  };

  // 引用元 : https://qiita.com/niba1122/items/7bc574b8629dff9ecbb7
  private mapToEnum<T>(enumObject: T, value: any): T[keyof T] | undefined {
    if (typeof enumObject === "object") {
      for (const key in enumObject) {
        if (Object.prototype.hasOwnProperty.call(enumObject, key) && enumObject[key] === value) {
          return enumObject[key];
        }
      }
    } else if (enumObject instanceof Array) {
      return enumObject.find(value);
    }
    /* 3.0.x
    } else if ((enumObject as any) instanceof Array) {
      return (enumObject as any[]).find(value)
    }
    */
  }

  private convertToCamel = (value: string): string => {
    const result = value.charAt(0).toUpperCase() + value.slice(1);
    return result.replace(/[-_](.)/g, (match, group1) => {
      return group1.toUpperCase();
    });
  };
}
